import { Helmet } from "react-helmet-async";
import { SITE_URL, SITE_NAME } from "@/lib/content";
import { API } from "@/lib/okkio";

const ORG_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_URL,
  description:
    "Piattaforma indipendente di analisi dei preventivi di ristrutturazione. Non è un'impresa edile e non vende ristrutturazioni.",
  slogan: "Prima di firmare, controlla tutto.",
};

const WEBSITE_LD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
};

const SOFTWARE_LD = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "OKKIO",
  applicationCategory: "FinanceApplication",
  operatingSystem: "Web",
  description:
    "Analisi indipendente del preventivo di ristrutturazione: prezzi, voci mancanti, costi nascosti e possibili extracosti.",
  offers: {
    "@type": "Offer",
    price: "9.99",
    priceCurrency: "EUR",
    description: "Report completo di un preventivo",
  },
};

export default function Seo({
  title,
  description,
  path = "/",
  breadcrumbs,
  faq,
  noindex = false,
  jsonLd = [],
}) {
  const url = `${SITE_URL}${path}`;
  const fullTitle = title ? `${title} — OKKIO` : "OKKIO — Prima di firmare, controlla tutto";
  const ogUrl = title
    ? `${API}/og?title=${encodeURIComponent(title)}&subtitle=${encodeURIComponent((description || "").slice(0, 90))}`
    : `${SITE_URL}/og-image.png`;
  const graphs = [ORG_LD, WEBSITE_LD, SOFTWARE_LD, ...jsonLd];

  if (breadcrumbs && breadcrumbs.length) {
    graphs.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.label,
        item: `${SITE_URL}${b.to}`,
      })),
    });
  }
  if (faq && faq.length) {
    graphs.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  return (
    <Helmet>
      <html lang="it" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,follow" />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content="it_IT" />
      <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogUrl} />

      {graphs.map((g, i) => (
        <script key={(g["@type"] || "ld") + "-" + i} type="application/ld+json">{JSON.stringify(g)}</script>
      ))}
    </Helmet>
  );
}
